import { useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { FileDown, ShieldCheck } from "lucide-react";

const hoje = new Date();
const primeiroDia = new Date(hoje.getFullYear(), hoje.getMonth(), 1).toISOString().slice(0, 10);
const ultimoDia = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0).toISOString().slice(0, 10);

/** Baixa um blob de um endpoint autenticado e dispara o download no navegador. */
async function baixar(url: string, params: Record<string, unknown>) {
  const resp = await apiClient.get(url, { params, responseType: "blob" });
  const cd: string = resp.headers["content-disposition"] ?? "";
  const m = /filename\*?=(?:UTF-8''|")?([^;"]+)/i.exec(cd);
  const nome = m ? decodeURIComponent(m[1].replace(/"/g, "")) : "download";
  const href = URL.createObjectURL(resp.data as Blob);
  const a = document.createElement("a");
  a.href = href; a.download = nome; a.click();
  URL.revokeObjectURL(href);
}

export default function ExportacoesPage() {
  const [inicio, setInicio] = useState(primeiroDia);
  const [fim, setFim] = useState(ultimoDia);
  const [baixando, setBaixando] = useState<string | null>(null);

  const gerar = async (tipo: "afd" | "aej", assinado: boolean) => {
    const chave = `${tipo}${assinado ? "-s" : ""}`;
    setBaixando(chave);
    try {
      await baixar(`/api/rh/ponto/${tipo}`, { inicio, fim, assinado });
      toast.success(`${tipo.toUpperCase()}${assinado ? " assinado" : ""} gerado.`);
    } catch (e: any) {
      toast.error(e.message ?? `Falha ao gerar o ${tipo.toUpperCase()}.`);
    } finally {
      setBaixando(null);
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-bold">Exportações fiscais (REP-P)</h1>
        <p className="text-sm text-muted-foreground">Arquivos da Portaria MTP 671/2021 para fiscalização e auditoria.</p>
      </div>

      <Card>
        <CardHeader><CardTitle>Período</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap items-end gap-4">
          <div className="space-y-1">
            <Label>Início</Label>
            <Input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Fim</Label>
            <Input type="date" value={fim} onChange={(e) => setFim(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>AFD — Arquivo Fonte de Dados</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Button variant="outline" disabled={baixando != null} onClick={() => gerar("afd", false)}>
            <FileDown className="h-4 w-4 mr-2" />{baixando === "afd" ? "Gerando…" : "Baixar AFD"}
          </Button>
          <Button disabled={baixando != null} onClick={() => gerar("afd", true)}>
            <ShieldCheck className="h-4 w-4 mr-2" />{baixando === "afd-s" ? "Gerando…" : "Baixar AFD assinado (.p7s)"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>AEJ — Arquivo Eletrônico de Jornada</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Button variant="outline" disabled={baixando != null} onClick={() => gerar("aej", false)}>
            <FileDown className="h-4 w-4 mr-2" />{baixando === "aej" ? "Gerando…" : "Baixar AEJ"}
          </Button>
          <Button disabled={baixando != null} onClick={() => gerar("aej", true)}>
            <ShieldCheck className="h-4 w-4 mr-2" />{baixando === "aej-s" ? "Gerando…" : "Baixar AEJ assinado (.p7s)"}
          </Button>
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        A assinatura CAdES AD-RB exige um certificado ICP-Brasil A1 (.pfx) configurado no servidor;
        sem ele, o arquivo é gerado sem o <code>.p7s</code>. Comprovante (PAdES) entra na próxima etapa.
      </p>
    </div>
  );
}
